import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { readDTEFacele } from '../../slices/warehouseGuide/thunk';

/**
 * Polls the Facele PDF endpoint every 5s up to 30s total.
 * Returns { status, blob, error }.
 * - status: 'idle' | 'loading' | 'ready' | 'timeout' | 'error'
 */
export default function useDtePdfPolling() {
	const dispatch = useDispatch();
	const [status, setStatus] = useState('idle');
	const [blob, setBlob] = useState(null);
	const [error, setError] = useState(null);
	const timeoutRef = useRef(null);
	const intervalRef = useRef(null);

	const stop = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	const start = useCallback(
		async dteId => {
			if (!dteId) return;
			setStatus('loading');
			setBlob(null);
			setError(null);

			const attemptFetch = async () => {
				try {
					const response = await dispatch(
						readDTEFacele({ file_type: 'pdf', dte_id: dteId })
					).unwrap();

					// response is an axios response with blob due to response interceptor differences on ExternalAPIClient
					const data = response?.data ?? response; // axios wraps in .data
					const contentTypeHeader =
						response?.headers?.['content-type'] ||
						response?.headers?.get?.('content-type');
					const contentDisposition =
						response?.headers?.['content-disposition'] ||
						response?.headers?.get?.('content-disposition');
					if (
						contentTypeHeader?.includes('application/pdf') ||
						/attachment/i.test(contentDisposition || '')
					) {
						if (data instanceof Blob) {
							setBlob(data);
							setStatus('ready');
							stop();
							return true;
						}
					}
					// Try to detect JSON (status) vs PDF
					if (data instanceof Blob) {
						if (data.type === 'application/pdf' || data.size > 0) {
							setBlob(data);
							setStatus('ready');
							stop();
							return true;
						}
					}
					// If blob but not pdf, try to parse text to see a JSON message
					if (
						data instanceof Blob &&
						data.type?.includes('application/json')
					) {
						const text = await data.text();
						try {
							const json = JSON.parse(text);
							if (json?.status && json?.status !== 'ready') {
								return false; // keep polling
							}
						} catch (_) {
							return false;
						}
					}
					return false;
				} catch (err) {
					// Keep polling on 404 or not-ready messages; stop on other errors
					const message = err?.message || String(err);
					if (
						/404/.test(message) ||
						/not\s*ready/i.test(message) ||
						/unavailable/i.test(message)
					) {
						return false;
					}
					setError(message);
					setStatus('error');
					stop();
					return true;
				}
			};

			// First try immediately
			const first = await attemptFetch();
			if (first) return;

			intervalRef.current = setInterval(async () => {
				const done = await attemptFetch();
				if (done) {
					clearInterval(intervalRef.current);
					intervalRef.current = null;
				}
			}, 5000);

			timeoutRef.current = setTimeout(() => {
				setStatus(prev => (prev === 'ready' ? prev : 'timeout'));
				stop();
			}, 30000);
		},
		[dispatch, stop]
	);

	useEffect(() => () => stop(), [stop]);

	return { start, status, blob, error, stop };
}
